import React, { useState, useEffect } from 'react';
import { Typography, Card, Form, Input, Button, message, Spin, Row, Col, Tooltip, Divider, Space, Alert } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { useQuery, useMutation } from 'react-query';
import { proteinService, ProteinAnalysisTask } from '@/api/proteinService';
import TaskResultRenderer from '@/components/results/TaskResultRenderer';
import { isSingleFastaFormat, extractSingleFastaSequence, isMultiFastaFormat } from '@/utils/sequenceUtils';
import SignalPeptideForm from '@/components/results/protein/basic/SignalPeptideForm';
import { taskManagerClient, TaskCreateRequest, getTasksByUser, TaskResponse } from '@/utils/taskManager';
import { getVisitorId } from '@/utils/session';

const { Title, Text } = Typography;
const { TextArea } = Input;

const formatTaskName = (name: string) => name?.replace(/\s*-\s*统一任务API\s*$/, '') || '';

const extractSequenceFromParams = (task: TaskResponse): string => {
  // 从原始参数中提取序列
  const params = (task as any)?.parameters;
  return params?.sequence || '';
};

const parseFastaInput = (input: string) => {
  if (!input) return [] as Array<{ id: string; sequence: string }>;
  const lines = input.trim().split(/\r?\n/);
  const entries: Array<{ id: string; sequence: string }> = [];
  let currentId = '';
  let currentSeq: string[] = [];

  const pushEntry = () => {
    if (!currentId && currentSeq.length === 0) return;
    const sequence = currentSeq.join('').replace(/\s+/g, '');
    if (sequence) {
      entries.push({ id: currentId || '', sequence });
    }
  };

  lines.forEach(line => {
    const trimmed = line.trim();
    if (!trimmed) return;
    if (trimmed.startsWith('>')) {
      pushEntry();
      currentId = trimmed.substring(1).trim();
      currentSeq = [];
    } else {
      currentSeq.push(trimmed);
    }
  });
  pushEntry();

  return entries;
};

const isAcceptedTaskResult = (result: any): boolean => {
  if (!result || typeof result !== 'object') return false;
  const successAccepted = result.success === true;
  const messageAccepted = typeof result.message === 'string' && result.message.includes('任务已接受');
  const statusAccepted = typeof result.status === 'string' && result.status.toLowerCase() === 'accepted';
  const hasTaskId = Boolean(result.task_id || result.taskId);
  const hasNoPayload = !result.results && !result.total_sequences && !result.data;
  return successAccepted && hasTaskId && (messageAccepted || statusAccepted) && hasNoPayload;
};

const normalizeTaskResult = (task: TaskResponse): TaskResponse => {
  if (!task) return task;
  const normalized = { ...task };
  let resultAny = normalized.result as any;
  
  if (resultAny?.results && Array.isArray(resultAny.results) && 
      resultAny.results.length === 1 && 
      resultAny.results[0]?.result?.results && 
      Array.isArray(resultAny.results[0].result.results)) {
    console.log('[PROTEIN] 检测到双重嵌套结果，进行扁平化处理');
    resultAny = resultAny.results[0].result;
    normalized.result = resultAny;
  }
  
  const sequence = extractSequenceFromParams(task);
  const fastaEntries = parseFastaInput(sequence);

  const enrichResultItem = (item: any, index: number) => {
    const baseResult = item?.result || item?.data || item || {};
    const sequenceId = item?.sequence_id || item?.name || fastaEntries[index]?.id || `序列${index + 1}`;
    const matchedEntry = fastaEntries.find(entry => entry.id === sequenceId) || fastaEntries[index];
    const enrichedResult = { ...baseResult };

    if (matchedEntry?.sequence && !enrichedResult.sequence) {
      enrichedResult.sequence = matchedEntry.sequence;
    }

    return {
      ...item,
      sequence_id: sequenceId,
      result: enrichedResult,
      success: item?.success !== false,
      error: item?.error || null
    };
  };

  if (resultAny?.results && Array.isArray(resultAny.results)) {
    const enrichedResults = resultAny.results.map(enrichResultItem);

    normalized.result = {
      ...resultAny,
      results: enrichedResults,
      total_sequences: resultAny.total_sequences ?? enrichedResults.length,
      successful_count: resultAny.successful_count ?? enrichedResults.filter((r: any) => r.success !== false).length,
      failed_count: resultAny.failed_count ?? enrichedResults.filter((r: any) => r.success === false).length,
      artifacts: resultAny.artifacts || resultAny.artifact || {}
    };

    if (!normalized.handler_name && resultAny.results[0]?.handler_name) {
      normalized.handler_name = resultAny.results[0].handler_name;
    }

    if (!normalized.service_name && resultAny.results[0]?.service_name) {
      normalized.service_name = resultAny.results[0].service_name;
    }
  } else if (resultAny?.batch && Array.isArray(resultAny.batch)) {
    const enrichedResults = resultAny.batch.map(enrichResultItem);

    normalized.result = {
      ...resultAny,
      results: enrichedResults,
      total_sequences: resultAny.total_sequences ?? enrichedResults.length,
      successful_count: resultAny.successful_count ?? enrichedResults.filter((r: any) => r.success !== false).length,
      failed_count: resultAny.failed_count ?? enrichedResults.filter((r: any) => r.success === false).length,
      artifacts: resultAny.artifacts || resultAny.artifact || {}
    };
  } else if (sequence && normalized.result && typeof normalized.result === 'object') {
    if (!(normalized.result as any).sequence) {
      (normalized.result as any).sequence = fastaEntries[0]?.sequence || sequence;
    }
  }
  
  // 也将序列添加到任务级别，供组件使用
  (normalized as any).sequence = sequence || fastaEntries[0]?.sequence || '';

  return normalized;
};

export default function ProteinPage() {
  const [form] = Form.useForm();
  const [taskResults, setTaskResults] = useState<TaskResponse[]>([]); // 存储完整任务结果
  const [currentTaskId, setCurrentTaskId] = useState<string | null>(null);
  const [isBatchProcessing, setIsBatchProcessing] = useState<boolean>(false); // 批量处理状态
  const [taskManagerTasks, setTaskManagerTasks] = useState<TaskResponse[]>([]);
  const [taskLoading, setTaskLoading] = useState(false);
  
  // 规范化结果组件的 taskId，确保与 componentRegistry 中的键一致
  const normalizeTaskId = (id?: string): string => {
    if (!id) return '';
    const map: Record<string, string> = {
      // 基础分析别名
      'protein_analysis/molecular_weight': 'molecular_weight',
      'basic_analysis/molecular_weight': 'molecular_weight',
      'basic_analysis/hydropathy': 'hydropathy',
      'basic_analysis/instability_index': 'instability_index',
      'basic_analysis/extinction_coefficient': 'extinction_coefficient',
      'basic_analysis/charge_profile': 'charge_profile',
      'basic_analysis/flexibility': 'flexibility',
      'amino_acid_conversion': 'amino_acid_convert',
      'basic_analysis/amino_acid_convert': 'amino_acid_convert',
      // 高级分析别名
      'signal_peptide': 'signal_prediction',
      'protein_analysis/signal_prediction': 'signal_prediction',
      'tm_prediction/predict': 'tm_prediction',
      'protein_analysis/tm_prediction': 'tm_prediction',
      'protein_analysis/ecoli_expression/predict': 'ecoli_expression',
      'protein_analysis/disorder/predict': 'disorder',
      'protein_analysis/ptm_predict_json': 'ptm_prediction'
    };
    if (map[id]) return map[id];
    // 如果是路径样式，递归去掉末尾的动作标识
    if (id.includes('/')) {
      const last = id.substring(id.lastIndexOf('/') + 1);
      if (['predict', 'analyze', 'analysis'].includes(last)) {
        return normalizeTaskId(id.substring(0, id.lastIndexOf('/')));
      }
      // 尝试用最后一段匹配已知映射
      return map[last] || last;
    }
    return id;
  };
  
  // 检查是否有疏水性计算结果
  const hasHydropathyResults = taskResults.some(task => 
    task.task_type === 'hydropathy' || 
    task.handler_name?.includes('hydropathy') ||
    task.service_name?.includes('hydropathy')
  );

  // 1. 从后端获取所有可用任务列表
  const { data: tasks, isLoading: isLoadingTasks, error: taskLoadError } = useQuery(
    'proteinAnalysisTasks', // 使用固定的查询键
    () => proteinService.getAnalysisTasks(),
    {
      onSuccess: (data) => {
        console.log('✅ 成功加载任务列表:', data?.length, '个任务', data);
      },
      onError: (error: any) => {
        console.error('❌ 加载任务失败:', error);
        message.error('加载分析任务失败: ' + error.message);
      },
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 0, // 不使用缓存
      cacheTime: 0  // 不缓存结果
    }
  );

  // 调试信息
  console.log('🔍 任务加载状态:', { 
    isLoadingTasks, 
    tasksCount: tasks?.length, 
    hasError: !!taskLoadError 
  });

  // 旧的直接服务调用逻辑已移除，现在统一使用任务管理器

  // 使用与RNAhybrid相同的轮询机制
  const pollTaskResult = async (taskId: string, maxAttempts: number = 30, intervalMs: number = 2000): Promise<any> => {
    const startTime = Date.now();
    console.log(`[PROTEIN] 开始轮询任务 ${taskId}，最大尝试次数: ${maxAttempts}，间隔: ${intervalMs}ms`);
    
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        // 使用公开的状态接口，不需要认证 - 动态获取API地址
        const apiBase = import.meta.env.VITE_TASK_MANAGER_URL?.replace(/\/$/, '') || '/task_manager';
        const response = await fetch(`${apiBase}/tasks/${taskId}/status`);
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        
        const task = await response.json();
        const elapsed = Date.now() - startTime;
        console.log(`[PROTEIN] 第 ${attempt + 1}/${maxAttempts} 次，耗时: ${elapsed}ms，任务状态:`, {
          id: task.id,
          status: task.status,
          progress: task.progress,
          hasResult: !!task.result
        });
        
        // 检查任务是否完成
        if ((task.status === 'COMPLETED' || task.status === 'completed') && task.result) {
          const totalTime = Date.now() - startTime;
          console.log(`[PROTEIN] 任务 ${taskId} 完成，总耗时: ${totalTime}ms`);
          return task.result;
        } else if (task.status === 'failed' || task.status === 'FAILED') {
          console.error('任务失败:', task.error);
          throw new Error(task.error || '任务执行失败');
        }
        
        // 等待后继续轮询
        if (attempt < maxAttempts - 1) {
          await new Promise(resolve => setTimeout(resolve, intervalMs));
        }
      } catch (error) {
        const elapsed = Date.now() - startTime;
        console.error(`[PROTEIN] 第 ${attempt + 1}/${maxAttempts} 次失败，耗时: ${elapsed}ms:`, error);
        if (attempt === maxAttempts - 1) {
          throw new Error(`任务 ${taskId} 轮询失败: ${error}`);
        }
      }
    }
    
    throw new Error(`任务 ${taskId} 在 ${maxAttempts} 次尝试后仍未完成`);
  };

  // 后台非阻塞轮询：提交后不阻塞UI，完成后自动加入结果
  const startBackgroundPolling = (taskId: string, displayName?: string) => {
    const taskLabel = displayName || '任务';
    const messageKey = `protein-task-${taskId}`;
    let attempt = 0;
    let intervalMs = 2000;
    const maxIntervalMs = 15000;

    const statusTextMap: Record<string, string> = {
      accepted: '已受理，等待调度...'
    , queued: '排队中...'
    , running: '正在处理...'
    , pending: '等待开始...'
    };

    const pollOnce = async () => {
      try {
        const apiBase = import.meta.env.VITE_TASK_MANAGER_URL?.replace(/\/$/, '') || '/task_manager';
        const resp = await fetch(`${apiBase}/tasks/${taskId}/status`);
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const taskStatus = await resp.json();
        const statusLower = String(taskStatus.status || '').toLowerCase();

        if (statusLower === 'completed') {
          try {
            const completedTask = await taskManagerClient.getTask(taskId);
            const rawResult = completedTask?.result;
            if (rawResult && !isAcceptedTaskResult(rawResult)) {
              const normalizedTask = normalizeTaskResult(completedTask);
              setTaskResults(prev => {
                const filtered = prev.filter(item => item.id !== taskId);
                return [...filtered, normalizedTask];
              });
              await loadTasks();
              message.success({ key: messageKey, content: `${taskLabel} 分析完成！`, duration: 3 });
              return;
            }
            // 任务已标记完成但结果尚未写入
            message.loading({ key: messageKey, content: `${taskLabel} 已完成，等待结果回写...`, duration: 0 });
            if (completedTask) {
              setTaskResults(prev => {
                const filtered = prev.filter(item => item.id !== taskId);
                return [...filtered, { ...completedTask, isPendingResult: true, pendingMessage: `${taskLabel} 已完成，正在等待结果写回...` } as any];
              });
            }
          } catch (error) {
            console.warn('[PROTEIN] 获取任务详情失败，稍后重试:', error);
            message.loading({ key: messageKey, content: `${taskLabel} 状态刷新失败，重试中...`, duration: 0 });
          }
          setTimeout(pollOnce, Math.min(intervalMs, maxIntervalMs));
          return;
        }

        if (['failed', 'cancelled', 'timeout'].includes(statusLower)) {
          await loadTasks();
          message.error({ key: messageKey, content: `${taskLabel} 执行${statusLower === 'failed' ? '失败' : statusLower === 'cancelled' ? '被取消' : '超时'}`, duration: 5 });
          return;
        }

        const statusTip = statusTextMap[statusLower] || '正在处理中...';
        message.loading({ key: messageKey, content: `${taskLabel} ${statusTip}`, duration: 0 });
        if (!taskResults.some(result => result.id === taskId)) {
          try {
            const currentTask = await taskManagerClient.getTask(taskId);
            setTaskResults(prev => {
              const filtered = prev.filter(item => item.id !== taskId);
              return [...filtered, { ...currentTask, isPendingResult: true, pendingMessage: `${taskLabel} ${statusTip}` } as any];
            });
          } catch (error) {
            console.warn('[PROTEIN] 预填充任务状态失败:', error);
          }
        }
      } catch (error) {
        console.error('[PROTEIN] 后台轮询失败，准备重试:', error);
        message.loading({ key: messageKey, content: `${taskLabel} 状态刷新失败，重试中...`, duration: 0 });
      }

      attempt += 1;
      intervalMs = Math.min(Math.floor(intervalMs * 1.2), maxIntervalMs);
      setTimeout(pollOnce, intervalMs);
    };

    message.loading({ key: messageKey, content: `${taskLabel} 已提交，等待调度...`, duration: 0 });
    pollOnce();
  };

  // 加载任务管理器任务列表
  const loadTasks = async () => {
    setTaskLoading(true);
    try {
      // 按服务端当前登录用户与服务名过滤（后端从认证上下文解析真实用户ID）
      const taskList = await getTasksByUser(undefined as any, 0, 100, undefined, 'protein_analysis', undefined);
      
      // 仅根据服务名过滤，移除旧的 visitorId 匹配
      const filteredTasks = taskList.filter((task: TaskResponse) => 
        task.service_name === 'protein_analysis'
      );
      
      setTaskManagerTasks(filteredTasks.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()));
      // 不再将历史已完成任务自动加入结果集，避免刷新或清空后又出现
      
    } catch (error) {
      console.error('加载任务列表失败:', error);
    } finally {
      setTaskLoading(false);
    }
  };

  
  // 添加定时轮询
  useEffect(() => {
    loadTasks();
    const interval = setInterval(() => {
      loadTasks();
    }, 5000); // 每5秒轮询一次
    return () => clearInterval(interval);
  }, []); // 保持空依赖数组，避免无限循环

  // 统一的任务提交处理函数
  const handleTaskSubmit = async (task: any) => {
    try {
      const values = await form.validateFields();
      const rawInput = values.sequence?.trim();
      if (!rawInput) {
        message.error('请输入蛋白质序列');
        return;
      }

      const baseName = (values.name || '').trim();
      const fastaEntries = parseFastaInput(rawInput);

      const buildFastaContent = () => {
        if (rawInput.startsWith('>')) {
          return rawInput;
        }
        const sequenceId = baseName ? baseName : 'seq1';
        return `>${sequenceId}\n${rawInput.replace(/\s+/g, '')}`;
      };

      const fastaContent = fastaEntries.length > 1 ? rawInput : buildFastaContent();

      const submitTask = async (sequenceContent: string, displayName: string) => {
        let taskConfig;

        if (task.module_id === 'tm_prediction') {
          taskConfig = {
            task_type: 'tm_prediction',
            handler_name: 'tm_prediction_predict_json',
            parameters: {
              sequence: sequenceContent,
              name: displayName || 'Analysis'
            }
          };
        } else if (task.module_id === 'signal_prediction') {
          taskConfig = {
            task_type: 'signal_prediction',
            handler_name: 'signal_prediction_predict_json',
            parameters: {
              sequence: sequenceContent,
              name: displayName || 'Analysis',
              organism: mapOrganismToBackend('Eukarya')
            }
          };
        } else if (task.module_id === 'ptm' || (typeof task.module_id === 'string' && task.module_id.startsWith('ptm_'))) {
          const handlerName = 'ptm_predict_json';
          taskConfig = {
            task_type: 'ptm',
            handler_name: handlerName,
              parameters: {
                sequence: sequenceContent,
                name: displayName || 'Analysis',
                ptm_type: values.ptm_type || (() => {
                  const ptmType = task.module_id.replace('ptm_', '');
                  const typeMapping: Record<string, string> = {
                    'hydroxylysine': 'Hydroxylysine',
                    'hydroxyproline': 'Hydroxyproline', 
                    'methylarginine': 'Methylarginine',
                    'methyllysine': 'Methyllysine',
                    'n-linked_glycosylation': 'N-linked_glycosylation',
                    'n6-acetyllysine': 'N6-acetyllysine',
                    'o-linked_glycosylation': 'O-linked_glycosylation',
                    'phosphoserine_phosphothreonine': 'Phosphoserine_Phosphothreonine',
                    'phosphotyrosine': 'Phosphotyrosine',
                    'pyrrolidone_carboxylic_acid': 'Pyrrolidone_carboxylic_acid',
                    's-palmitoyl_cysteine': 'S-palmitoyl_cysteine',
                    'sumoylation': 'SUMOylation',
                    'ubiquitination': 'Ubiquitination'
                  };
                  return typeMapping[ptmType] || 'Phosphotyrosine';
                })()
              }
          };
        } else if (task.module_id === 'disorder') {
          taskConfig = {
            task_type: 'disorder_prediction',
            handler_name: 'disorder_predict_json',
            parameters: {
              sequence: sequenceContent,
              name: displayName || 'Analysis'
            }
          };
        } else if (task.module_id === 'ecoli_expression') {
          taskConfig = {
            task_type: 'expression_prediction',
            handler_name: 'ecoli_expression_predict_json',
            parameters: {
              sequence: sequenceContent,
              name: displayName || 'Analysis'
            }
          };
        } else {
          const handlerName = task.endpoint.split('/').pop() || '';
          taskConfig = {
            task_type: 'basic_analysis',
            handler_name: handlerName,
            parameters: {
              sequence: sequenceContent,
              name: displayName || 'Analysis',
              ...(handlerName === 'amino_acid_convert_json' && { conversion_type: 'auto' })
            }
          };
        }

        const taskData: TaskCreateRequest = {
          name: `${task.name} - ${displayName || 'Analysis'}`,
          description: `执行${task.name}分析`,
          service_name: 'protein_analysis',
          ...taskConfig,
          priority: 5,
          timeout: 3600
        };

        const createdTask = await taskManagerClient.createTask(taskData);
        console.log('任务已创建:', createdTask.id, displayName);

        startBackgroundPolling(createdTask.id, displayName);
      };

      const displayName = baseName || (fastaEntries.length > 1 ? '批量分析' : (fastaEntries[0]?.id || 'Analysis'));
      await submitTask(fastaContent, displayName);
    } catch (error) {
      console.error('创建任务失败:', error);
      message.error('任务创建失败');
    }
  };

  // 信号肽预测任务提交处理
  const handleSignalPeptideSubmit = async (task: any, organism: string) => {
    try {
      const values = await form.validateFields();
      const sequence = values.sequence.trim();
      
      // 处理单序列FASTA格式
      let processedSequence = sequence;
      let sequenceName = values.name;
      
      if (isSingleFastaFormat(sequence) && !isMultiFastaFormat(sequence)) {
        const { id, sequence: extractedSeq } = extractSingleFastaSequence(sequence);
        processedSequence = extractedSeq;
        sequenceName = id || sequenceName;
      }
      
      // 通过任务管理系统提交
      const taskConfig = {
        task_type: 'signal_prediction',
        handler_name: 'signal_prediction_predict_json',
        parameters: {
          sequence: processedSequence,
          name: sequenceName,
          organism: mapOrganismToBackend(organism)
        }
      };
      
      const taskData: TaskCreateRequest = {
        name: `${task.name} - ${sequenceName || 'Analysis'}`,
        description: `执行${task.name}分析`,
        service_name: 'protein_analysis',
        ...taskConfig,
        // 不传递user_id，让后端自动分配
        priority: 5,
        timeout: 3600
      };
      
      const createdTask = await taskManagerClient.createTask(taskData);
      console.log('信号肽预测任务已创建:', createdTask.id);
      
      // 后台非阻塞轮询
      startBackgroundPolling(createdTask.id, sequenceName || '信号肽预测');
      
    } catch (info) {
      console.log('表单验证失败:', info);
    }
  };

  // 映射前端organism值到后端需要的格式
  const mapOrganismToBackend = (organism: string): string => {
    switch ((organism || '').toLowerCase()) {
      case 'euk':
      case 'eukarya':
        return 'Eukarya';
      case 'gramp':
      case 'gram-positive':
        return 'Gram-positive';
      case 'gramn':
      case 'gram-negative':
        return 'Gram-negative';
      default:
        return 'Other';
    }
  };

  // 删除单个结果
  const handleDeleteResult = (index: number) => {
    setTaskResults(prevResults => prevResults.filter((_, i) => i !== index));
  };

  // 清空所有结果
  const handleClearAllResults = () => {
    if (window.confirm('确定要清空所有分析结果吗？')) {
      setTaskResults([]);
    }
  };

  // 渲染结果 - 直接使用完整 Task 对象
  const renderResults = () => {
    return taskResults.map((task, index) => (
      <div key={`${task.id}-${index}`} style={{ marginBottom: 16 }}>
        <TaskResultRenderer 
          task={task}
          showRaw={false}
          onDeleteResult={() => handleDeleteResult(index)}
        />
      </div>
    ));
  };
  
  // 渲染任务按钮
  const renderTaskButtons = () => {
    console.log('🎨 renderTaskButtons 调用:', { 
      tasks,
      tasksType: typeof tasks,
      tasksLength: tasks?.length,
      isArray: Array.isArray(tasks)
    });
    
    if (!tasks || tasks.length === 0) {
      console.log('🔄 显示加载中...');
      return <Spin tip="加载任务列表..." />;
    }

    // 按模块分组任务 - 显示所有可用任务
    const taskGroups: Record<string, ProteinAnalysisTask[]> = {};
    tasks.forEach(task => {
      if (task.module_id === 'signal_prediction') {
        return;
      }
      // 隐藏通用PTM预测按钮，只显示具体的PTM子类型按钮
      if (task.module_id === 'ptm') {
        return;
      }
      if (!taskGroups[task.module_name]) {
        taskGroups[task.module_name] = [];
      }
      taskGroups[task.module_name].push(task);
    });

    // 过滤掉空的任务组
    const nonEmptyGroups = Object.entries(taskGroups).filter(([_, groupTasks]) => groupTasks.length > 0);

    return (
      <div style={{ marginBottom: 16 }}>
        {nonEmptyGroups.map(([groupName, groupTasks]) => (
          <Card 
            key={groupName} 
            title={groupName} 
            style={{ marginBottom: 16 }}
            size="small"
          >
            <Space wrap>
              {groupTasks.map(task => (
                <Button
                  key={task.module_id}
                  type={currentTaskId === task.module_id + task.endpoint ? 'primary' : 'default'}
                  onClick={() => handleTaskSubmit(task)}
                  loading={currentTaskId === task.module_id + task.endpoint}
                  disabled={!!currentTaskId || isBatchProcessing}
                >
                  {formatTaskName(task.name)}
                </Button>
              ))}
            </Space>
          </Card>
        ))}
      </div>
    );
  };

  // 渲染信号肽预测表单
  const renderSignalPeptideForm = () => {
    if (!tasks) return null;
    
    const signalPeptideTask = tasks.find(task => task.endpoint.includes('signal_prediction'));
    if (!signalPeptideTask) return null;

    const signalConfig = {
      module_id: signalPeptideTask.module_id,
      name: signalPeptideTask.name,
      endpoint: signalPeptideTask.endpoint,
      path: signalPeptideTask.endpoint,
      service_name: 'protein_analysis',
      handler_name: 'signal_prediction_predict_json'
    };

    return (
      <SignalPeptideForm
        task={signalConfig as any}
        onSubmit={handleSignalPeptideSubmit}
        isLoading={!!currentTaskId}
      />
    );
  };



  return (
    <div style={{ maxWidth: 1200, margin: '0 auto' }}>
      <Typography style={{ marginBottom: 24, textAlign: 'center' }}>
        <Title level={2}>蛋白质分析</Title>
      </Typography>

      <Card>
        <Form form={form} layout="vertical">
          <Form.Item 
            name="sequence" 
            label="蛋白质序列" 
            rules={[{ required: true, message: '请输入蛋白质序列' }]}
          >
            <TextArea 
              rows={10} 
              placeholder={"支持格式：\n1. 纯文本序列：直接输入氨基酸序列\n示例：MKTLLLTLVVVTIVCLDLGYT\n\n2. 单序列FASTA：标准FASTA格式\n示例：>sp|P12345|PROT_HUMAN\nMKTLLLTLVVVTIVCLDLGYT\n\n3. 多序列FASTA：批量分析\n示例：>seq1\nMKTLLLTLVVVT\n>seq2\nMKTLLLTLVVVT"} 
            />
          </Form.Item>
          
          <Form.Item name="name" label="序列名称（可选）">
            <Input placeholder="例如：my_protein_sequence（仅对纯文本序列有效）" />
          </Form.Item>
        </Form>
      </Card>

      <Card title="可用分析任务" style={{ marginTop: 24 }}>
        {(() => {
          console.log('🔍 Card渲染条件检查:', { 
            tasks, 
            tasksType: typeof tasks,
            tasksLength: tasks?.length,
            isArray: Array.isArray(tasks),
            condition: !tasks || tasks.length === 0
          });
          
          if (!tasks || tasks.length === 0) {
            console.log('📊 显示加载状态');
            return (
              <div style={{ textAlign: 'center', margin: '40px 0' }}>
                <Spin size="large" tip="加载分析功能中..." />
              </div>
            );
          } else {
            console.log('📦 显示任务按钮');
            return (
              <>
                {renderTaskButtons()}
                {renderSignalPeptideForm()}
              </>
            );
          }
        })()}
      </Card>

      {taskResults.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <Title level={3}>分析结果</Title>
            <Space>
              <Button icon={<ReloadOutlined />} onClick={loadTasks}>
                刷新任务
              </Button>
              <Button danger onClick={handleClearAllResults}>
                清空结果
              </Button>
            </Space>
          </div>
          {renderResults()}
        </div>
      )}
    </div>
  );
}